import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ExternalLink, FlaskConical, KeyRound, ShieldAlert, Users } from 'lucide-react';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function normalizeLabel(value) {
  return String(value || '—').replaceAll('_', ' ');
}

function maskEmail(value) {
  const email = String(value || '').trim();
  if (!email) return '—';
  const atIndex = email.indexOf('@');
  if (atIndex <= 0) return '***';
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);
  if (local.length <= 1) return `***@${domain}`;
  if (local.length === 2) return `${local[0]}***@${domain}`;
  return `${local[0]}***${local[local.length - 1]}@${domain}`;
}

function SummaryCard({ title, value, detail }) {
  return (
    <article className="border border-zinc-200 bg-white p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{title}</p>
      <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-zinc-900">{value}</p>
      <p className="mt-2 text-sm leading-6 text-zinc-600">{detail}</p>
    </article>
  );
}

function Badge({ children, tone = 'zinc' }) {
  const toneClass = {
    emerald: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-300 bg-amber-50 text-amber-800',
    red: 'border-red-300 bg-red-50 text-red-700',
    sky: 'border-sky-200 bg-sky-50 text-sky-800',
    zinc: 'border-zinc-200 bg-zinc-50 text-zinc-700',
  }[tone] || 'border-zinc-200 bg-zinc-50 text-zinc-700';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${toneClass}`}>{children}</span>;
}

export default function InternalAccountDetail() {
  const { organizationId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: responseData } = await api.get(`/admin/accounts/${organizationId}`);
      setData(responseData);
    } catch (requestError) {
      setError(requestError?.response?.status === 404
        ? 'This internal account could not be found.'
        : requestError?.response?.data?.error || 'Internal account details could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => data?.summary || {}, [data]);
  const organization = data?.organization || null;

  const counts = useMemo(() => [
    { title: 'Members', value: summary.member_count || 0, detail: `${summary.admin_member_count || 0} admin · ${summary.reviewer_member_count || 0} reviewer` },
    { title: 'Verifications', value: summary.verification_count || 0, detail: `${summary.approved_verification_count || 0} approved` },
    { title: 'Identities', value: summary.identity_count || 0, detail: 'Internal idem identity records.' },
    { title: 'Digital key accounts', value: summary.digital_key_account_count || 0, detail: `${summary.active_digital_key_account_count || 0} active` },
    { title: 'Login requests', value: summary.login_request_count || 0, detail: 'Internal lab request activity.' },
  ], [summary]);

  return (
    <div data-testid="internal-account-detail-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Account Detail" showDashboard={false} />

      <div className="mb-6">
        <Link to="/internal/accounts" className="text-sm text-zinc-500 hover:text-black hover:underline">← Back to internal accounts</Link>
        <p className="mt-4 text-xs font-mono uppercase tracking-widest text-zinc-500">Internal admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">{organization?.name || 'Internal account detail'}</h1>
        <p className="mt-2 max-w-3xl text-sm text-zinc-600">Safe internal metadata for one workspace. The page never shows raw evidence, secrets, or cross-tenant admin controls.</p>
      </div>

      <section className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
          <div>
            <p className="font-medium">Internal-only workspace detail</p>
            <p className="mt-1">This view is limited to masked metadata and safe summaries. No API keys, webhook secrets, document images, selfies, OCR/MRZ data, or tokens are exposed.</p>
          </div>
        </div>
      </section>

      {loading ? (
        <p className="mt-6 text-sm text-zinc-500">Loading internal account details…</p>
      ) : error ? (
        <p className="mt-6 text-sm text-red-700">{error}</p>
      ) : organization ? (
        <>
          <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            {counts.map((card) => (
              <SummaryCard key={card.title} {...card} />
            ))}
          </section>

          <section className="mt-6 border border-zinc-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-semibold">Organization profile</h2>
                <p className="mt-1 text-sm text-zinc-600">Workspace state, onboarding, and safe activity timing.</p>
              </div>
              <Badge tone={organization.onboarding_completed ? 'emerald' : 'amber'}>
                {organization.onboarding_completed ? 'Onboarding complete' : 'Onboarding incomplete'}
              </Badge>
            </div>

            <dl className="mt-4 grid gap-x-6 md:grid-cols-2">
              <div className="grid grid-cols-[minmax(9rem,0.75fr)_minmax(0,1.6fr)] gap-4 border-b border-zinc-100 py-3">
                <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Organization ID</dt>
                <dd className="min-w-0 break-all text-sm text-zinc-800">{organization.id}</dd>
              </div>
              <div className="grid grid-cols-[minmax(9rem,0.75fr)_minmax(0,1.6fr)] gap-4 border-b border-zinc-100 py-3">
                <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Workspace type</dt>
                <dd className="min-w-0 text-sm text-zinc-800">{normalizeLabel(organization.workspace_type)}</dd>
              </div>
              <div className="grid grid-cols-[minmax(9rem,0.75fr)_minmax(0,1.6fr)] gap-4 border-b border-zinc-100 py-3">
                <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Organization type</dt>
                <dd className="min-w-0 text-sm text-zinc-800">{organization.organization_type ? normalizeLabel(organization.organization_type) : '—'}</dd>
              </div>
              <div className="grid grid-cols-[minmax(9rem,0.75fr)_minmax(0,1.6fr)] gap-4 border-b border-zinc-100 py-3">
                <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Last activity</dt>
                <dd className="min-w-0 text-sm text-zinc-800">{formatDate(organization.last_activity_at)}</dd>
              </div>
              <div className="grid grid-cols-[minmax(9rem,0.75fr)_minmax(0,1.6fr)] gap-4 border-b border-zinc-100 py-3">
                <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Job function</dt>
                <dd className="min-w-0 text-sm text-zinc-800">{organization.onboarding_job_function || '—'}</dd>
              </div>
              <div className="grid grid-cols-[minmax(9rem,0.75fr)_minmax(0,1.6fr)] gap-4 border-b border-zinc-100 py-3">
                <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Use case</dt>
                <dd className="min-w-0 text-sm text-zinc-800">{organization.onboarding_use_case || '—'}</dd>
              </div>
              <div className="grid grid-cols-[minmax(9rem,0.75fr)_minmax(0,1.6fr)] gap-4 border-b border-zinc-100 py-3">
                <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Volume</dt>
                <dd className="min-w-0 text-sm text-zinc-800">{organization.onboarding_volume || '—'}</dd>
              </div>
              <div className="grid grid-cols-[minmax(9rem,0.75fr)_minmax(0,1.6fr)] gap-4 border-b border-zinc-100 py-3">
                <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Created at</dt>
                <dd className="min-w-0 text-sm text-zinc-800">{formatDate(organization.created_at)}</dd>
              </div>
            </dl>
          </section>

          <section className="mt-6 border border-zinc-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-semibold">Members</h2>
                <p className="mt-1 text-sm text-zinc-600">Masked email addresses only.</p>
              </div>
              <Link to="/internal/identities" className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
                Open identities
              </Link>
            </div>

            {data.members?.length ? (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="py-2 pr-4 font-medium">Display name</th>
                      <th className="py-2 pr-4 font-medium">Masked email</th>
                      <th className="py-2 pr-4 font-medium">Role</th>
                      <th className="py-2 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.members.map((member) => (
                      <tr key={member.id} className="border-b border-zinc-100 last:border-b-0">
                        <td className="py-3 pr-4 text-zinc-800">{member.display_name || '—'}</td>
                        <td className="py-3 pr-4 font-mono text-xs text-zinc-600">{member.email_masked || '—'}</td>
                        <td className="py-3 pr-4 text-zinc-600">{normalizeLabel(member.role)}</td>
                        <td className="py-3 text-zinc-600">{formatDate(member.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">No members were returned for this account.</p>
            )}
          </section>

          <section className="mt-6 border border-zinc-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-semibold">Recent verifications</h2>
                <p className="mt-1 text-sm text-zinc-600">Verification metadata only. No raw evidence is included.</p>
              </div>
            </div>
            {data.recent_verifications?.length ? (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="py-2 pr-4 font-medium">Verification</th>
                      <th className="py-2 pr-4 font-medium">Method</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Purpose</th>
                      <th className="py-2 pr-4 font-medium">Created</th>
                      <th className="py-2 font-medium">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_verifications.map((verification) => (
                      <tr key={verification.verification_id} className="border-b border-zinc-100 last:border-b-0">
                        <td className="py-3 pr-4 font-mono text-xs text-zinc-600">{verification.verification_id}</td>
                        <td className="py-3 pr-4 text-zinc-600">{normalizeLabel(verification.verification_method)}</td>
                        <td className="py-3 pr-4 text-zinc-600">{normalizeLabel(verification.verification_status)}</td>
                        <td className="py-3 pr-4 text-zinc-600">{normalizeLabel(verification.purpose)}</td>
                        <td className="py-3 pr-4 text-zinc-600">{formatDate(verification.created_at)}</td>
                        <td className="py-3 text-zinc-600">{formatDate(verification.completed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">No verification history available.</p>
            )}
          </section>

          <section className="mt-6 border border-zinc-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-semibold">Recent identity records</h2>
                <p className="mt-1 text-sm text-zinc-600">Internal identity references and safe lifecycle metadata.</p>
              </div>
              <Link to="/internal/digital-key-accounts" className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
                Open digital key accounts
              </Link>
            </div>
            {data.recent_identities?.length ? (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="py-2 pr-4 font-medium">Identity</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Source verification</th>
                      <th className="py-2 pr-4 font-medium">Created</th>
                      <th className="py-2 font-medium">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_identities.map((identity) => (
                      <tr key={identity.identity_id} className="border-b border-zinc-100 last:border-b-0">
                        <td className="py-3 pr-4">
                          <Link to={`/internal/identities/${identity.public_id || identity.identity_id}`} className="font-medium text-zinc-800 hover:text-black hover:underline">
                            {identity.public_id}
                          </Link>
                          <div className="mt-1 font-mono text-[10px] text-zinc-500">{identity.identity_id}</div>
                        </td>
                        <td className="py-3 pr-4 text-zinc-600">{normalizeLabel(identity.status)}</td>
                        <td className="py-3 pr-4 text-zinc-600">{identity.source_verification_id || '—'}</td>
                        <td className="py-3 pr-4 text-zinc-600">{formatDate(identity.created_at)}</td>
                        <td className="py-3 text-zinc-600">{formatDate(identity.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">No recent identities found for this workspace.</p>
            )}
          </section>

          <section className="mt-6 border border-zinc-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-semibold">Digital key accounts</h2>
                <p className="mt-1 text-sm text-zinc-600">Linked account state and safe assurance metadata.</p>
              </div>
              <Link to="/internal/oidc-lab" className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                <FlaskConical className="h-3.5 w-3.5" strokeWidth={1.5} />
                Open OIDC lab
              </Link>
            </div>
            {data.digital_key_accounts?.length ? (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="py-2 pr-4 font-medium">Account</th>
                      <th className="py-2 pr-4 font-medium">Identity</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Eligibility</th>
                      <th className="py-2 pr-4 font-medium">Created</th>
                      <th className="py-2 font-medium">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.digital_key_accounts.map((account) => (
                      <tr key={account.account_id} className="border-b border-zinc-100 last:border-b-0">
                        <td className="py-3 pr-4 font-mono text-xs text-zinc-600">{account.account_id}</td>
                        <td className="py-3 pr-4">
                          <Link to={`/internal/identities/${account.identity_public_id || account.identity_id}`} className="font-medium text-zinc-800 hover:text-black hover:underline">
                            {account.identity_public_id || '—'}
                          </Link>
                          <div className="mt-1 font-mono text-[10px] text-zinc-500">{account.identity_id}</div>
                        </td>
                        <td className="py-3 pr-4 text-zinc-600">{normalizeLabel(account.status)}</td>
                        <td className="py-3 pr-4 text-zinc-600">{normalizeLabel(account.eligibility_status)}</td>
                        <td className="py-3 pr-4 text-zinc-600">{formatDate(account.created_at)}</td>
                        <td className="py-3 text-zinc-600">{formatDate(account.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">No digital key accounts found for this workspace.</p>
            )}
          </section>

          <section className="mt-6 border border-zinc-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-semibold">Recent login requests</h2>
                <p className="mt-1 text-sm text-zinc-600">Lab-only request metadata and final state.</p>
              </div>
              <Link to="/internal/authenticator-preview" className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                Open authenticator preview
              </Link>
            </div>
            {data.recent_login_requests?.length ? (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="py-2 pr-4 font-medium">Request</th>
                      <th className="py-2 pr-4 font-medium">Identity</th>
                      <th className="py-2 pr-4 font-medium">Account</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Assurance</th>
                      <th className="py-2 pr-4 font-medium">Created</th>
                      <th className="py-2 font-medium">Finalized</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_login_requests.map((request) => (
                      <tr key={request.request_id} className="border-b border-zinc-100 last:border-b-0">
                        <td className="py-3 pr-4 font-mono text-xs text-zinc-600">{request.request_id}</td>
                        <td className="py-3 pr-4 text-zinc-600">{request.identity_public_id || request.identity_id}</td>
                        <td className="py-3 pr-4 font-mono text-xs text-zinc-600">{request.account_id}</td>
                        <td className="py-3 pr-4 text-zinc-600">{normalizeLabel(request.status)}</td>
                        <td className="py-3 pr-4 text-zinc-600">{normalizeLabel(request.requested_assurance)}</td>
                        <td className="py-3 pr-4 text-zinc-600">{formatDate(request.created_at)}</td>
                        <td className="py-3 text-zinc-600">
                          {formatDate(request.approved_at || request.denied_at || request.expired_at || request.cancelled_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">No recent login requests found for this workspace.</p>
            )}
          </section>

          <section className="mt-6 flex flex-wrap gap-2">
            <Link to="/internal/identities" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open identities</Link>
            <Link to="/internal/digital-key-accounts" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open digital key accounts</Link>
            <Link to="/internal/fas-readiness" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open readiness matrix</Link>
          </section>
        </>
      ) : null}
    </div>
  );
}
